import React from 'react';
import { Shield, LogOut, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const formatTime = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0
    ? `${m}:${String(s).padStart(2, '0')}`
    : `${s}s`;
};

/**
 * Full-screen overlay shown when the user is about to be timed out.
 * The countdown ring shrinks as time runs out.
 */
const SessionTimeoutModal = ({ secondsLeft, warningSeconds = 120, onStayLoggedIn, onLogout }) => {
  const pct = Math.max(0, Math.min(1, secondsLeft / warningSeconds));
  // SVG ring: circumference = 2π × 38 ≈ 238.76
  const CIRC = 238.76;
  const dash = pct * CIRC;
  const colour = secondsLeft > 60 ? '#f97316' : secondsLeft > 30 ? '#ef4444' : '#991b1b';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4 text-center">
        {/* Shield icon */}
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center">
            <Shield className="h-7 w-7 text-orange-500" />
          </div>
        </div>

        <h2 className="text-xl font-bold text-gray-800 mb-1">Session Expiring</h2>
        <p className="text-sm text-gray-500 mb-6">
          You've been inactive for a while. For security, you'll be logged out automatically.
        </p>

        {/* Countdown ring */}
        <div className="flex justify-center mb-6">
          <div className="relative w-24 h-24">
            <svg viewBox="0 0 100 100" className="-rotate-90 w-full h-full">
              {/* Track */}
              <circle cx="50" cy="50" r="38" fill="none" stroke="#e5e7eb" strokeWidth="8" />
              {/* Progress */}
              <circle
                cx="50" cy="50" r="38"
                fill="none"
                stroke={colour}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${CIRC}`}
                style={{ transition: 'stroke-dasharray 0.9s linear, stroke 0.5s' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold tabular-nums" style={{ color: colour }}>
                {formatTime(secondsLeft)}
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wide">remaining</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            onClick={onStayLoggedIn}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Stay Logged In
          </Button>
          <Button
            variant="ghost"
            onClick={onLogout}
            className="w-full text-gray-500 hover:text-red-600 hover:bg-red-50 gap-2"
          >
            <LogOut className="h-4 w-4" />
            Log Out Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;
